/* ------------------------------------------------------------------
   njord-track.js — FunnelAuditor beacon (WP7)

   Rendered by snippets/njg-tracking.liquid, which owns the master switch.
   Installed in: staging 157073965229, cart test 156432367789, live
   154588610733. All three must carry it — the first two split traffic
   ~50/50 via Intelligems, so a partial install reads as pixel loss.

   It posts to the funnel-track endpoint THROUGH THE SHOPIFY APP PROXY,
   so the request is first-party to the store domain. A third-party
   request has its script-set storage capped at 7 days by Safari's ITP,
   which is shorter than the purchase cycle. That cap is the single
   reason this design exists.

   It touches nothing LongGame owns. fbc capture is untouched (C6), and
   nothing here runs in checkout (C2) — Checkout Extensibility does not
   load theme assets, so that holds without an exclusion.

   KILL SWITCH: append ?njg_off=1 to any store URL and hard-refresh.
   Persists per-browser until ?njg_off=0.
------------------------------------------------------------------ */
(function njgTrackBoot() {
  "use strict";

  var NJG_VERSION = "1.0.0";

  var PROXY_PATH = "/apps/njg/track"; // App Proxy subpath — operator-configured
  var SESSION_KEY = "njg_sid";
  var FIRST_TOUCH_KEY = "njg_ft";
  var OFF_KEY = "njg_off";
  var DEBUG_KEY = "njg_debug";
  var SESSION_TTL_MS = 30 * 60 * 1000; // 30 min inactivity
  var ATC_DEDUPE_MS = 1000;

  /* The join key's guard. utm_content carries non-ad values in the wild —
     a measured 101 of 130 tagged orders held `sag_organic` (Shopify's FB/IG
     organic marker) and one held `link_in_bio`. Only a long digit string is
     an ad ID. Everything else is discarded rather than stored as noise.

     The live theme re-stamps stored UTMs onto every internal link forever
     (its own appendUTMToLinks), so `sag_organic` propagates site-wide. This
     guard is the only thing keeping it out of the data. */
  function cleanAdId(v) {
    return typeof v === "string" && /^\d{6,}$/.test(v) ? v : null;
  }

  /* Priority chain: our own parameter first, then Meta's, then the UTM
     field. Each is guarded, so a junk value in a high-priority slot falls
     through instead of poisoning the row. */
  function adIdFromUrl(search) {
    var p = new URLSearchParams(search || "");
    return (
      cleanAdId(p.get("njg_adid")) ||
      cleanAdId(p.get("ad_id")) ||
      cleanAdId(p.get("utm_content"))
    );
  }

  /* Google's campaign, read from tw_campaign — which carries Google's own
     {campaignid} ValueTrack value and is present on 97% of Google-driven
     orders. NOT gad_campaignid: that returns non-existent IDs for all three
     PMax campaigns in this account, and PMax is where most Google orders come
     from.

     Its own field, not folded into ad_id: ad_id joins meta_ads.id, this joins
     google_ads.campaign_id. Merging two identifier spaces would produce rows
     that join to nothing and read as tracking loss. */
  function googleCampaignFromUrl(search) {
    return cleanAdId(new URLSearchParams(search || "").get("tw_campaign"));
  }

  function read(key) {
    try {
      return JSON.parse(localStorage.getItem(key) || "null");
    } catch (e) {
      return null;
    }
  }
  function store(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode — the cookie is the remaining carrier */
    }
  }

  /* Flags readable from the URL so they can be set without devtools, and
     written through to storage so they survive navigation — reproducing a
     cart bug takes more than one page. setItem only, never removeItem. */
  function njgFlag(key) {
    var q = null;
    try {
      q = new URLSearchParams(location.search).get(key);
    } catch (e) {}
    if (q === "1" || q === "0") {
      store(key, q);
      return q === "1";
    }
    return read(key) === "1";
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return "s-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  /* The session cookie is set HERE, by script, and not by funnel-track.
     The Shopify App Proxy strips Set-Cookie from responses (and Cookie from
     requests) as a deliberate security measure, so the server cannot set or
     read one — verified 2026-08-01.

     Script-set is fine for this specific job. Safari's ITP caps script-set
     storage at 7 days, which is why the original design wanted the server to
     do it — but a session lives 30 MINUTES. The cap never binds. It would
     bind on first touch, which is why first touch does not rely on this and
     is made durable by the cart attribute instead.

     Scoped to the registrable domain so it reaches quiz.njordgearco.com
     without putting the session id in a URL where it could be copied out of
     an address bar and shared. */
  function readSessionCookie() {
    try {
      var m = String(document.cookie || "").match(/(^|;)\s*njg_sid\s*=\s*([^;]+)/);
      return m ? decodeURIComponent(m[2]) : null;
    } catch (e) {
      return null;
    }
  }
  function writeSessionCookie(id) {
    try {
      document.cookie =
        "njg_sid=" + encodeURIComponent(id) + "; path=/; domain=." +
        registrable(currentHost()) + "; max-age=" + Math.floor(SESSION_TTL_MS / 1000) +
        "; SameSite=Lax" + (location.protocol === "https:" ? "; Secure" : "");
    } catch (e) {
      /* cookies blocked — localStorage and the URL are the remaining paths */
    }
  }

  function currentHost() {
    try {
      return location.hostname || new URL(location.href).hostname;
    } catch (e) {
      return "";
    }
  }

  /* Last two labels. quiz.njordgearco.com and njordgearco.com share one;
     toughtalktech.com does not. Good enough here because we only ever
     compare hosts we own — no public-suffix edge cases reach this. */
  function registrable(host) {
    var parts = String(host || "").split(".");
    return parts.slice(-2).join(".");
  }

  function isOurHost(href) {
    try {
      var h = new URL(href, location.href).hostname;
      return /(^|\.)njordgearco\.com$/.test(h) || /(^|\.)toughtalktech\.com$/.test(h);
    } catch (e) {
      return false;
    }
  }

  /* Only links that LEAVE the current registrable domain get a session id.
     Same-site continuity comes from the cookie above, which covers every
     subdomain, survives localStorage being blocked, and — unlike a URL —
     cannot be shared.

     Decorating same-site links is what let a shopper copy their address bar,
     text the link to a friend, and merge both people into one attribution
     session. Cross-domain hops still need the URL because a cookie cannot
     cross to toughtalktech.com. */
  function needsDecoration(href) {
    if (!isOurHost(href)) return false;
    try {
      var target = new URL(href, location.href).hostname;
      return registrable(target) !== registrable(currentHost());
    } catch (e) {
      return false;
    }
  }

  /* sessionStorage is ORIGIN-SCOPED, and quiz.njordgearco.com and
     toughtalktech.com are separate origins from the store. A session id kept
     only there is lost at every hop between them, which is exactly where the
     funnel we are trying to measure runs. Storage is a cache; the cookie is
     the same-site transport and the URL is the cross-domain one. */
  function getSession() {
    var fromUrl = null;
    try {
      fromUrl = new URLSearchParams(location.search).get(SESSION_KEY);
    } catch (e) {}
    var cached = read(SESSION_KEY);
    var live = !!(cached && cached.id && Date.now() - (cached.at || 0) < SESSION_TTL_MS);

    /* Adopt an inbound id ONLY with no live session of our own. Without this,
       a shared link hijacks an existing visitor mid-session and credits their
       order to whoever sent the link. */
    if (fromUrl && !live) {
      return keep(fromUrl);
    }
    if (live) {
      return keep(cached.id);
    }
    /* Cookie before a fresh id: it is the only carrier that survives a hop to
       quiz.njordgearco.com, and the only one left when localStorage throws
       (Safari "Block All Cookies" throws on the property access itself). */
    var fromCookie = readSessionCookie();
    if (fromCookie) {
      return keep(fromCookie);
    }
    return keep(uuid());
  }

  /* Write through to both carriers on every resolution so the two never drift
     and whichever survives can reseed the other. */
  function keep(id) {
    store(SESSION_KEY, { id: id, at: Date.now() });
    writeSessionCookie(id);
    return id;
  }

  /* First touch is written once and never overwritten. A visitor who returns
     via a second ad still credits the ad that introduced them; overwriting
     here would silently convert this into last-touch. */
  function recordFirstTouch(adId) {
    if (!adId) return;
    if (read(FIRST_TOUCH_KEY)) return;
    store(FIRST_TOUCH_KEY, { ad_id: adId, at: new Date().toISOString() });
  }

  function decorateLinks(sessionId) {
    document.addEventListener(
      "click",
      function njgDecorateClick(e) {
        var a = e.target && e.target.closest && e.target.closest("a[href]");
        if (!a || !needsDecoration(a.href)) return;
        try {
          var u = new URL(a.href, location.href);
          if (u.protocol !== "http:" && u.protocol !== "https:") return;
          if (a.hasAttribute && a.hasAttribute("download")) return;
          /* A same-page #fragment whose query would change turns a smooth
             scroll into a full document reload — and fires a second pageview
             into GA4, Meta and Triple Whale for one tap. */
          if (u.pathname === location.pathname && u.search === location.search) return;
          u.searchParams.set(SESSION_KEY, sessionId);
          a.href = u.toString();
        } catch (err) {
          /* leave the link alone rather than break navigation */
        }
      },
      true,
    );
  }

  var sent = {};
  var lastBeacon = null;
  var warnedHttp = false;
  var debugOn = false;
  /* Views fire once per page; add_to_cart legitimately repeats — a cart
     drawer opens without navigating, so every add after the first would
     otherwise be lost. */
  var REPEATABLE = { add_to_cart: true };

  function send(eventName, sessionId, adId) {
    if (sent[eventName] && !REPEATABLE[eventName]) return;
    sent[eventName] = true;

    var payload = JSON.stringify({
      event_name: eventName,
      session_id: sessionId,
      ad_id: adId,
      google_campaign_id: googleCampaignFromUrl(location.search),
      page_url: location.href,
      variant: window.njgVariant || null,
    });

    /* Query params are inert server-side (funnel-track routes on method, not
       path) and make the request self-describing in a support agent's
       Network tab. */
    var url = PROXY_PATH + "?e=" + encodeURIComponent(eventName) + "&v=" + NJG_VERSION;
    lastBeacon = { event: eventName, at: new Date().toISOString(), status: null, ok: null };

    try {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
        credentials: "same-origin",
        // Keeps the beacon from competing with the LCP image for bandwidth.
        priority: "low",
      })
        .then(function njgBeaconDone(r) {
          r = r || {};
          lastBeacon.status = r.status || 0;
          lastBeacon.ok = r.ok === true;
          /* fetch RESOLVES on 404 — it only rejects on network failure. Without
             this the App Proxy being unmounted is completely silent, on every
             pageview, forever. One line, once per page load. */
          if (r.status >= 400 && !warnedHttp) {
            warnedHttp = true;
            console.warn(
              "[njg-track " + NJG_VERSION + "] beacon returned HTTP " + r.status +
                " from " + PROXY_PATH + " — the Shopify App Proxy is probably not " +
                "configured, so nothing is being recorded. To switch this script " +
                "off: add ?njg_off=1 to the URL and hard-refresh.",
            );
          }
          if (debugOn) console.log("[njg-track] " + eventName + " → " + lastBeacon.status);
        })
        .catch(function njgBeaconFail(err) {
          /* Network-level failure is the shopper's connection, not our bug.
             Recorded for debug(), never surfaced to a real customer. */
          lastBeacon.ok = false;
          lastBeacon.error = String(err);
          if (debugOn) console.warn("[njg-track] " + eventName + " failed", err);
        });
    } catch (e) {
      lastBeacon.ok = false;
      lastBeacon.error = String(e);
      /* never let tracking break a storefront page */
    }
  }

  /* Prefer the template name Liquid renders into window.njgTemplate. The URL
     alone cannot tell a PDP from a landing page: Shopify serves products at
     /collections/{c}/products/{p} when reached from a collection grid, which
     a /^\/products\// test misses and logs as lp_view — silently corrupting
     the exact funnel step this page exists to measure. */
  function pageEvent() {
    var tpl = window.njgTemplate;
    if (typeof tpl === "string" && tpl) {
      if (tpl.indexOf("product") === 0) return "pdp_view";
      return "lp_view";
    }
    var p = location.pathname;
    if (/(^|\/)products\/[^/]+/.test(p)) return "pdp_view";
    return "lp_view";
  }

  function njgDisabled(reason) {
    return {
      version: NJG_VERSION,
      enabled: false,
      reason: reason,
      send: function () {},
      debug: function () {
        var d = { version: NJG_VERSION, enabled: false, reason: reason };
        console.log("[njg-track " + NJG_VERSION + "] OFF — " + reason, d);
        return d;
      },
      _cleanAdId: cleanAdId,
      _adIdFromUrl: adIdFromUrl,
      _isOurHost: isOurHost,
    };
  }

  function init() {
    // Double-inclusion guard: two copies would double-post and double-decorate.
    if (window.njgTrack && window.njgTrack.version) return;

    if (njgFlag(OFF_KEY)) {
      window.njgTrack = njgDisabled("njg_off flag set in this browser");
      return;
    }
    /* The theme editor reloads sections constantly; each is a fresh document
       and would post a fresh view. */
    if (window.Shopify && window.Shopify.designMode) {
      window.njgTrack = njgDisabled("Shopify theme editor (designMode)");
      return;
    }
    debugOn = njgFlag(DEBUG_KEY) || !!(window.njgSettings && window.njgSettings.debug);

    var sessionId = getSession();
    var adId = adIdFromUrl(location.search);
    var adIdSource = adId ? "url" : null;
    recordFirstTouch(adId);
    var ft = read(FIRST_TOUCH_KEY);
    if (!adId) {
      adId = ft && ft.ad_id ? ft.ad_id : null;
      adIdSource = adId ? "first_touch" : "none";
    }

    decorateLinks(sessionId);
    send(pageEvent(), sessionId, adId);

    var lastAtcAt = 0;
    function sendAtc() {
      var now = Date.now();
      if (now - lastAtcAt < ATC_DEDUPE_MS) return; // click + submit are one add
      lastAtcAt = now;
      sent.add_to_cart = false;
      send("add_to_cart", sessionId, adId);
    }

    /* Two listeners because neither alone is sufficient. The click handler
       catches ordinary button presses; the submit handler catches the APEX
       mobile sticky bar, whose button is type="button" OUTSIDE the form and
       calls form.requestSubmit() — that dispatches submit, never a matching
       click, so click alone misses the flagship mobile ATC path entirely.
       sendAtc() dedupes the overlap.

       [name="add"] is scoped to the cart-add form: unscoped it would match
       any app widget with that name. */
    document.addEventListener(
      "click",
      function njgAtcClick(e) {
        var el = e.target && e.target.closest && e.target.closest(
          'form[action*="/cart/add"] [type="submit"], ' +
            'form[action*="/cart/add"] [name="add"], [data-njg-atc]',
        );
        if (el) sendAtc();
      },
      true,
    );
    document.addEventListener(
      "submit",
      function njgAtcSubmit(e) {
        var f = e.target;
        if (f && f.action && String(f.action).indexOf("/cart/add") !== -1) sendAtc();
      },
      true,
    );

    window.njgTrack = {
      version: NJG_VERSION,
      enabled: true,
      send: function (name) { send(name, sessionId, adId); },
      sessionId: sessionId,
      adId: adId,
      firstTouchAt: ft && ft.at ? ft.at : null,
      debug: function () {
        var info = {
          version: NJG_VERSION,
          enabled: true,
          endpoint: PROXY_PATH,
          sessionId: sessionId,
          adId: adId,
          adIdSource: adIdSource, // "url" | "first_touch" | "none"
          firstTouch: read(FIRST_TOUCH_KEY),
          eventsSent: Object.keys(sent),
          lastBeacon: lastBeacon,
          cartStamp: window.njgCartStamp
            ? {
                version: window.njgCartStamp.version,
                lastStatus: window.njgCartStamp.lastStatus,
                lastAttrs: window.njgCartStamp.lastAttrs,
              }
            : "NOT INSTALLED",
          killSwitch: "add ?njg_off=1 to the URL, then hard-refresh",
        };
        console.log("[njg-track " + NJG_VERSION + "]", info);
        return info;
      },
      _cleanAdId: cleanAdId,
      _adIdFromUrl: adIdFromUrl,
      _isOurHost: isOurHost,
      _needsDecoration: needsDecoration,
    };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
